import type Owner from '@ember/owner';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import type { BufferedChangeset } from 'ember-changeset/types';
import saveSendMessage from 'parog-web/components/chat/utils/save-send-message';
import type { ForcBaseArgs } from 'parog-web/components/forc/abstract/base-component';
import JournalEventBasedComponent from 'parog-web/components/forc/abstract/journal-event-based-component';
import Provider from 'parog-web/components/forc/provider';
import Content from 'parog-web/components/prescription/bio-exam-edition-form/content';
import Preview from 'parog-web/components/prescription/bio-exam-edition-form/preview';
import BioExamPrescribedJournalEvent from 'parog-web/models/bio-exam-prescribed-journal-event';
import type AnalyticsService from 'parog-web/services/analytics';
import type SemeiaSessionService from 'parog-web/services/session';
interface Args extends ForcBaseArgs {
  changeset: BufferedChangeset;
  oldBioExamPrescription: BioExamPrescribedJournalEvent | undefined;
  isCreationFromTemplate: boolean;
}

/* eslint-disable parog-web/migrate-non-patch-forms */
export default class BioExamPrescriptionEditionFormComponent extends JournalEventBasedComponent<Args> {
  static override forcDisplayName = "Prescription d'examens biologiques";
  static override fullComponentId = 'prescription/bio-exam-edition-form';
  static override journalEventClass = BioExamPrescribedJournalEvent;

  @service declare analytics: AnalyticsService;
  @service declare router: RouterService;
  @service declare session: SemeiaSessionService;

  declare chatMessage: string;

  // Always dirty because we always are in creation mode
  override get isDirty() {
    return true;
  }

  constructor(owner: Owner, args: Args) {
    super(owner, args);

    this.chatMessage =
      'Bonjour, \nJe viens de vous éditer une nouvelle ordonnance. Elle est jointe à ce message. \nBonne journée.';
  }

  async _openPrescriptionInNewTab(
    savedModel: BioExamPrescribedJournalEvent
  ): Promise<void> {
    // Open the PDF prescription in a new tab
    if (savedModel) {
      this.router.replaceWith(
        'medics-web.protected.patients.patient.feature.component',
        this.model,
        'prescription',
        'bio-exam-display'
      );
    } else {
      throw new Error(
        `Impossible to open the PDF prescription because there is no JournalEvent found corresponding to the type : bio-exam-prescribed-journal-event}`
      );
    }
  }

  onCancelPrescription = () => {
    this.router.transitionTo(
      'medics-web.protected.patients.patient.dashboard',
      (this.args.changeset.data as BioExamPrescribedJournalEvent).patient
    );
  };

  override async save(): Promise<void> {
    this.shouldForceQuit = true;
    // The validation doesn't work if we don't launch it manually
    await this.args.changeset.validate();

    if (this.args.changeset.get('isValid')) {
      await this.args.changeset.save();
      await this.draft.onDidSave();

      const savedModel = this.args.changeset
        .data as BioExamPrescribedJournalEvent;
      if (savedModel.patient.authorizesAppUsage) {
        await saveSendMessage(
          { willSendMessage: true, chatMessage: this.chatMessage },
          {
            store: this.store,
            patient: savedModel.patient,
            analytics: this.analytics
          }
        );
        await this.store
          .createRecord('patient-chat-message-sent-journal-event', {
            occurredAt: new Date(),
            triggeringJournalEvent: savedModel,
            patient: savedModel.patient,
            body: 'Nouvelle ordonnance reçue'
          })
          .save();
      }

      const alertsTriggered = this.store.peekAll(
        'alert-triggered-journal-event'
      );
      const alert = alertsTriggered.find(
        alert =>
          alert.subType == 'bio-exam-prescription' &&
          alert.triggeringJournalEvent instanceof
            BioExamPrescribedJournalEvent &&
          alert.triggeringJournalEvent.id ==
            this.args.oldBioExamPrescription?.id
      );

      if (alert?.isUnprocessed) {
        await alert.processAlert({
          actionTaken: 'bio-exam-prescription-renewed'
        });
      }

      this.router.transitionTo(
        'medics-web.protected.patients.patient.prescriptions.bio-exam-display',
        savedModel.patient.id,
        savedModel.id
      );
    }
  }

  <template>
    <Provider @forcComponent={{this}} @isTextWidthLimitDisabled={{true}}>
      <:content as |Form|>
        <div class='ui horizontal basic equal width segments'>
          <div class='ui segment'>
            <Content
              @isCreationFromTemplate={{@isCreationFromTemplate}}
              @changeset={{@changeset}}
              @form={{Form}}
            />
          </div>
          <div class='ui segment'>
            <Preview @changeset={{@changeset}} />
          </div>
        </div>

      </:content>
      <:footer as |Footer|>
        <Footer.CancelButton
          @onClick={{this.onCancelPrescription}}
          @title='Annuler'
        />
        <Footer.SubmitButton
          @title='Sauvegarder et envoyer ordonnance au patient'
        />
      </:footer>
    </Provider>
  </template>
}
